import _ from "lodash";
import Vue from "vue";
import { V2_API } from "../../../api/chat";
import { publishMessage, SystemMessageTypes } from "../../../utils/systemPubSub";
import { listContactMessagesV3 } from "../../../api/chat";
import moment from "moment";

const getActiveView = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get("view") || "chat";
};

const getSortFromStorage = () => {
    try {
        const sortField = localStorage.getItem("contactSortField");
        const sortDir = localStorage.getItem("contactSortDir");
        return {
            field: sortField || "lastActionTime",
            dir: sortDir || "DESC",
        };
    } catch (error) {
        return {
            field: "lastActionTime",
            dir: "DESC",
        };
    }
};

const initialState = {
    cachedContacts: {},
    multiChatView: {
        enabled: false,
        type: "agent",
        contacts: [],
    },
    contacts: [],
    filters: [],
    selectedContact: null,
    selectedState: "all",
    selectedSortField: getSortFromStorage().field,
    selectedSortDir: getSortFromStorage().dir,
    counts: {
        all: 0,
        open: 0,
        closed: 0,
    },
    activeView: getActiveView(),
    selectedFilterId: null,
    messagesByContact: {}, // { [contactId]: { data: Message[], ts: number, totalPage, totalCount, offset, limit, loading } }
    whatsappContactStatus: {}, // { [contactId]: { status: string } }
    paginationCursor: {
        prevToken: null,
        nextToken: null,
        firstPageToken: null,
    },
};

const contact = {
    namespaced: true,
    state: initialState,
    mutations: {
        SET_COUNTS(state, counts) {
            state.counts = counts;
        },
        ADD_CONTACT(state, contact) {
            const newContact = {
                ...contact,
                _createdAt: new Date(),
            };

            const contactIndex = state.contacts.findIndex((c) => c._id === contact._id);
            if (contactIndex !== -1) {
                Vue.set(state.contacts, contactIndex, newContact);
            } else {
                state.contacts.push(newContact);
            }

            const sorted = _.orderBy(
                state.contacts,
                ['vipCustomer', state.selectedSortField],
                ['desc', state.selectedSortDir.toLowerCase()]
            );
            state.contacts.splice(0, state.contacts.length, ...sorted);

            const multiChatContactIndex = state.multiChatView.contacts.findIndex((c) => c._id === contact._id);
            if (multiChatContactIndex !== -1) {
                Vue.set(state.multiChatView.contacts, multiChatContactIndex, newContact);
            } else {
                state.multiChatView.contacts.push(newContact);
            }
        },
        REMOVE_CONTACT(state, contactId) {
            if (state.selectedContact?._id === contactId) {
                state.selectedContact = null;
            }

            const contactIdx = state.contacts.findIndex((c) => c._id === contactId);
            if (contactIdx !== -1) {
                state.contacts.splice(contactIdx, 1);
            }

            const multiChatContactIdx = state.multiChatView.contacts.findIndex((c) => c._id === contactId);
            if (multiChatContactIdx !== -1) {
                state.multiChatView.contacts.splice(multiChatContactIdx, 1);
            }

            Vue.delete(state.whatsappContactStatus, contactId);
            Vue.delete(state.cachedContacts, contactId);
            Vue.delete(state.messagesByContact, contactId);
        },
        SET_CONTACTS(state, contacts) {
            if (!Array.isArray(contacts)) {
                state.contacts.splice(0, state.contacts.length);
                return;
            }

            const now = new Date();
            const processedContacts = contacts.map((c) => ({
                ...c,
                _createdAt: c._createdAt || now,
            }));
            const sorted = _.orderBy(
                processedContacts,
                ['vipCustomer', state.selectedSortField],
                ['desc', state.selectedSortDir.toLowerCase()]
            );
            state.contacts.splice(0, state.contacts.length, ...sorted);
        },
        UPDATE_CONTACT(state, contact) {
            const contactIndex = state.contacts.findIndex((c) => c._id === contact._id);
            if (contactIndex !== -1) {
                Vue.set(state.contacts, contactIndex, {
                    ...contact,
                    inited: _.get(state.contacts[contactIndex], "inited", false),
                });
                
                const sorted = _.orderBy(
                    state.contacts,
                    ['vipCustomer', state.selectedSortField],
                    ['desc', state.selectedSortDir.toLowerCase()]
                );
                state.contacts.splice(0, state.contacts.length, ...sorted);
            }

            const cachedContact = state.cachedContacts[contact._id];
            if (cachedContact) {
                Vue.set(state.cachedContacts, contact._id, {
                    ...contact,
                    _createdAt: new Date(),
                    inited: _.get(cachedContact, "inited", false),
                });
            }

            const multiChatContactIdx = state.multiChatView.contacts.findIndex((c) => c._id === contact._id);
            if (multiChatContactIdx !== -1) {
                Vue.set(state.multiChatView.contacts, multiChatContactIdx, contact);
            }

            if (state.selectedContact?._id === contact._id && contactIndex !== -1) {
                state.selectedContact = state.contacts.find((c) => c._id === contact._id);
            }
        },
        UPDATE_CONTACT_FIELDS(state, payload) {
            const contactId = _.get(payload, "contactId");
            const set = _.get(payload, "set", {});
            const idx = state.contacts.findIndex((c) => c && c._id === contactId);

            const isAlreadyUpdated = _.get(state.contacts[idx], "unread", true) === set.unread;
            if(set.unread === false && !isAlreadyUpdated) {
                V2_API.setContactViewed(contactId).catch(_.noop);
            }

            if (idx !== -1) {
                const updated = Object.assign({}, state.contacts[idx], set);
                Vue.set(state.contacts, idx, updated);
                
                const sorted = _.orderBy(
                    state.contacts,
                    ['vipCustomer', state.selectedSortField],
                    ['desc', state.selectedSortDir.toLowerCase()]
                );
                state.contacts.splice(0, state.contacts.length, ...sorted);
            }

            const cachedContact = state.cachedContacts[contactId];
            if (cachedContact) {
                const updated = Object.assign({}, cachedContact, set);
                Vue.set(state.cachedContacts, contactId, updated);
            }

            const multiChatContactIdx = state.multiChatView.contacts.findIndex((c) => c._id === contactId);
            if (multiChatContactIdx !== -1) {
                const contact = state.multiChatView.contacts[multiChatContactIdx];
                const updated = Object.assign({}, contact, set);
                Vue.set(state.multiChatView.contacts, multiChatContactIdx, updated);
            }

            if (state.selectedContact?._id === contactId && idx !== -1) {
                const newIdx = state.contacts.findIndex((c) => c && c._id === contactId);
                if (newIdx !== -1) {
                    state.selectedContact = state.contacts[newIdx];
                }
            }
        },
        SET_CONTACT_UNREAD(state, payload) {
            const contactId = _.get(payload, "contactId");
            const unread = _.get(payload, "unread");
            const idx = state.contacts.findIndex((c) => c && c._id === contactId);

            const isAlreadyUpdated = _.get(state.contacts[idx], "unread", true) === unread;
            if(unread === false && !isAlreadyUpdated) {
                V2_API.setContactViewed(contactId).catch(_.noop);
             }

            if (idx !== -1) {
                const updated = Object.assign({}, state.contacts[idx], { unread: unread });
                Vue.set(state.contacts, idx, updated);
            }

            const cachedContact = state.cachedContacts[contactId];
            if (cachedContact) {
                const updated = Object.assign({}, cachedContact, { unread: unread });
                Vue.set(state.cachedContacts, contactId, updated);
            }

            const multiChatContactIdx = state.multiChatView.contacts.findIndex((c) => c._id === contactId);
            if (multiChatContactIdx !== -1) {
                const updated = Object.assign({}, state.multiChatView.contacts[multiChatContactIdx], {
                    unread: unread,
                });
                Vue.set(state.multiChatView.contacts, multiChatContactIdx, updated);
            }
           
        },
        SET_FILTERS(state, filters) {
            state.filters = filters;
        },
        SET_SELECTED_CONTACT(state, selectedContact) {
            state.selectedContact = selectedContact;
        },
        SET_SELECTED_STATE(state, selectedState) {
            state.selectedState = selectedState;
        },
        SET_SELECTED_SORT_FIELD(state, selectedSortField) {
            state.selectedSortField = selectedSortField;
            try {
                localStorage.setItem("contactSortField", selectedSortField);
            } catch (error) {
                // ignore
            }
        },
        SET_SELECTED_SORT_DIR(state, selectedSortDir) {
            state.selectedSortDir = selectedSortDir;
            try {
                localStorage.setItem("contactSortDir", selectedSortDir);
            } catch (error) {
                // ignore
            }
        },
        SET_ACTIVE_VIEW(state, activeView) {
            state.activeView = activeView;
        },
        SET_SELECTED_FILTER_ID(state, selectedFilter) {
            state.selectedFilterId = selectedFilter;
        },
        SET_PAGINATION_CURSOR(state, payload) {
            const updatingFields = Object.keys(payload);
            updatingFields.forEach((field) => {
                state.paginationCursor[field] = payload[field];
            });
        },
        APPEND_MESSAGE(state, payload) {
            const contactId = _.get(payload, "contactId");
            const message = _.get(payload, "message");
            const windows = _.get(payload, "windows");
            const limit = 100;
            if (!contactId || !message) return;

            let bucket = state.messagesByContact[contactId];
            if (!bucket) {
                bucket = { data: [], ts: Date.now() };
                Vue.set(state.messagesByContact, contactId, bucket);
            }

            const uniqKey = message.uniqMessageId || message._id;
            const existsIndex = bucket.data.findIndex((m) => {
                const k = m && (m.uniqMessageId || m._id);
                return k && uniqKey && k === uniqKey;
            });
            if (existsIndex === -1) {
                bucket.data.push(message);
            } else {
                Vue.set(bucket.data, existsIndex, message);
            }

            bucket.data = _.orderBy(bucket.data, ["createdDate"], ["asc"]);
            bucket.data = _.uniqBy(bucket.data, (m) => m.uniqMessageId || m._id);

            const shouldKeepAllMessages = (() => {
                if (state.selectedContact && state.selectedContact._id === contactId) {
                    return true;
                }
                if (state.multiChatView && state.multiChatView.enabled && Array.isArray(state.multiChatView.contacts)) {
                    const isInMultiChatView = state.multiChatView.contacts.some((contact) => {
                        return contact && contact._id === contactId;
                    });
                    if (isInMultiChatView) {
                        return true;
                    }
                }

                if (windows) {
                    return Object.values(windows).some((window) => {
                        return window && window.payload && window.payload.contactId === contactId;
                    });
                }
                return false;
            })();

            if (!shouldKeepAllMessages && bucket.data.length > limit) {
                bucket.data = bucket.data.slice(-limit);
            }

            const tsStr = _.get(message, "createdDate");
            bucket.ts = tsStr ? new Date(tsStr).getTime() : Date.now();
        },
        APPEND_MULTI_MESSAGE(state, payload) {
            const contactId = _.get(payload, "contactId");
            const messages = _.get(payload, "messages", []);
            const windows = _.get(payload, "windows");
            const limit = 100;
            if (!contactId || !Array.isArray(messages) || messages.length === 0) return;

            let bucket = state.messagesByContact[contactId];
            if (!bucket) {
                bucket = { data: [], ts: Date.now() };
                Vue.set(state.messagesByContact, contactId, bucket);
            }

            let latestTs = bucket.ts || 0;
            messages.forEach((message) => {
                const uniqKey = message.uniqMessageId || message._id;
                const existsIndex = bucket.data.findIndex((m) => {
                    const k = m && (m.uniqMessageId || m._id);
                    return k && uniqKey && k === uniqKey;
                });
                if (existsIndex === -1) {
                    bucket.data.push(message);
                } else {
                    Vue.set(bucket.data, existsIndex, message);
                }
                const tsStr = _.get(message, "createdDate");
                const ts = tsStr ? new Date(tsStr).getTime() : Date.now();
                if (ts > latestTs) latestTs = ts;
            });

            bucket.data = _.orderBy(bucket.data, ["createdDate"], ["asc"]);
            bucket.data = _.uniqBy(bucket.data, (m) => m.uniqMessageId || m._id);

            const shouldKeepAllMessages = (() => {
                if (state.selectedContact && state.selectedContact._id === contactId) {
                    return true;
                }
                if (state.multiChatView && state.multiChatView.enabled && Array.isArray(state.multiChatView.contacts)) {
                    const isInMultiChatView = state.multiChatView.contacts.some((contact) => {
                        return contact && contact._id === contactId;
                    });
                    if (isInMultiChatView) {
                        return true;
                    }
                }

                if (windows) {
                    return Object.values(windows).some((window) => {
                        return window && window.payload && window.payload.contactId === contactId;
                    });
                }
                return false;
            })();

            if (!shouldKeepAllMessages && bucket.data.length > limit) {
                bucket.data = bucket.data.slice(-limit);
            }

            bucket.ts = latestTs;
        },
        UPDATE_MESSAGE_PARTIAL(state, payload) {
            const contactId = _.get(payload, "contactId");
            const uniqMessageId = _.get(payload, "uniqMessageId");
            const updatedFields = _.get(payload, "updatedFields", {});
            if (!contactId || !uniqMessageId) return;
            const bucket = state.messagesByContact[contactId];
            if (!bucket || !Array.isArray(bucket.data)) return;
            const messageIndex = bucket.data.findIndex((m) => {
                const k = m && (m.uniqMessageId || m._id);
                return k && k === uniqMessageId;
            });
            if (messageIndex !== -1) {
                const updated = Object.assign({}, bucket.data[messageIndex], updatedFields);
                Vue.set(bucket.data, messageIndex, updated);
            }
        },
        SET_MESSAGE_DELETED(state, payload) {
            const contactId = _.get(payload, "contactId");
            const uniqMessageId = _.get(payload, "uniqMessageId");
            if (!contactId || !uniqMessageId) return;
            const bucket = state.messagesByContact[contactId];
            if (!bucket || !Array.isArray(bucket.data)) return;
            const messageIndex = bucket.data.findIndex((m) => {
                const k = m && (m.uniqMessageId || m._id);
                return k && k === uniqMessageId;
            });
            if (messageIndex !== -1) {
                const copy = Object.assign({}, bucket.data[messageIndex], { isDeleted: true });
                Vue.set(bucket.data, messageIndex, copy);
            }
        },
        SET_MULTI_CHAT_VIEW(state, data) {
            state.multiChatView = data;
        },
        TRIM_CONTACT_MESSAGES(state, payload) {
            const contactIds = Array.isArray(payload) ? payload : [payload];
            const limit = 100;
            console.log("TRIM_CONTACT_MESSAGES", { contactIds, limit });
            contactIds.forEach((contactId) => {
                if (!contactId) return;
                const bucket = state.messagesByContact[contactId];
                if (bucket && Array.isArray(bucket.data) && bucket.data.length > limit) {
                    const trimmedData = bucket.data.slice(-limit);
                    Vue.set(state.messagesByContact, contactId, {
                        ...bucket,
                        data: _.uniqBy(trimmedData, (m) => m.uniqMessageId || m._id),
                    });
                }
            });
        },
        SET_WHATSAPP_CONTACT_STATUS(state, payload) {
            const { channelId, whatsappNumber, status } = payload;
            Vue.set(state.whatsappContactStatus, `${channelId}_${whatsappNumber}`, status);
        },
        RESET_STATE(state) {
            state.contacts = [];
        },
        CLEAR_CACHED_CONTACTS(state) {
            state.cachedContacts = Object.keys(state.cachedContacts).filter((key) => state.cachedContacts[key]._id !== state.selectedContact?._id).reduce((acc, key) => {
                acc[key] = state.cachedContacts[key];
                return acc;
            }, {});
        },
    },
    getters: {
        selectedFilter: (state) => {
            return state.filters.find((filter) => filter.id === state.selectedFilterId);
        },
        hashContacts: (state) => {
            return _.keyBy(state.contacts, "_id");
        },
        hashFilters: (state) => {
            return _.keyBy(state.filters, "_id");
        },
        getContactByConversationId: (state) => (conversationId) => {
            return state.contacts.find((contact) => contact.lastConversation._id === conversationId);
        },
        getFilterById: (state) => (filterId) => {
            return state.filters.find((filter) => filter._id === filterId);
        },
        getMessagesForContact: (state) => (contactId) => {
            var bucket = state.messagesByContact[contactId];
            return bucket && Array.isArray(bucket.data) ? bucket.data : [];
        },
        storeContacts: (state) => (contactId) => {
           let contact = state.cachedContacts[contactId];
           if(!contact){
            contact = state.contacts.find((contact) => contact._id === contactId);
           }
           return contact;
        },
    },
    actions: {
        setCounts({ commit }, counts) {
            commit("SET_COUNTS", counts);
        },
        resetState({ commit }) {
            commit("RESET_STATE");
        },
        clearCachedContacts({ commit }) {
            commit("CLEAR_CACHED_CONTACTS");
        },
        getContactById({ state }, contactId) {
            let contact = state.cachedContacts[contactId];
            if (!contact) contact = state.contacts.find((contact) => contact._id === contactId);
            return contact;
        },
        async fetchContacts({}, params) {
            const contacts = await V2_API.getContacts(params);
            return contacts;
        },
        async getFilteredContacts(
            { state, getters, commit },
            payload
        ) {
            const {
                source,
                sort,
                group,
                statusState,
                limit = 20,
                nextToken,
                prevToken,
                appendContacts = false,
                withoutCount = false,
                cursor,
                metadata,
                state: payloadState,
                filters,
                count = false,
            } = payload;
            
            if (!appendContacts && !nextToken && !prevToken && !cursor?.nextToken && !cursor?.offset) {
                commit("SET_CONTACTS", []);
                commit("SET_PAGINATION_CURSOR", { firstPageToken: null });
            }
            
            const selectedFilter = getters.selectedFilter;
            const filterType = selectedFilter?.type;
            const filterId = selectedFilter?.id;

            const selectedSortField = sort?.field || state.selectedSortField;
            const selectedSortDir = sort?.direction || state.selectedSortDir;
            const selectedState = payloadState || statusState || state.selectedState;

            const params = {
                metadata: metadata || {
                    source: source || "unknown",
                    withoutCount: withoutCount ? "true" : "false",
                    count: count ? "true" : "false",
                },
                sort: {
                    field: selectedSortField,
                    direction: selectedSortDir,
                },
                cursor: cursor || {
                    limit,
                },
                state: selectedState,
            };
            
            // Eğer filters parametresi varsa inbox ekleme (edit mode için)
            if (!filters || filters.length === 0) {
                params.inbox = {
                    type: filterType,
                    id: filterId,
                };
            } else {
                params.filters = filters;
            }
            
            if (group) params.group = group;
            if (!cursor) {
                if (nextToken) params.cursor.nextToken = nextToken;
                if (prevToken) params.cursor.prevToken = prevToken;
                if (state.paginationCursor.firstPageToken)
                    params.cursor.firstPageToken = state.paginationCursor.firstPageToken;
            }
            
            const data = await V2_API.getContacts(params);

            const contacts = appendContacts ? [...state.contacts, ...data.contacts] : data.contacts;
            const selectedContact = state.selectedContact;
            const contact = contacts.find((c) => c._id === selectedContact?._id);
            if (!contact) state.selectedContact = null;

            commit("SET_COUNTS", data.counts);
            commit("SET_CONTACTS", contacts);
            commit("SET_PAGINATION_CURSOR", {
                nextToken: data.cursor.nextToken,
                prevToken: data.cursor.prevToken,
                firstPageToken: data.cursor.firstPageToken,
            });
            return data;
        },
        async getContactListFilters({ commit }) {
            const storageKey = "contactListFilters";
            try {
                const storedFilters = localStorage.getItem(storageKey);
                if (storedFilters) {
                    const parsedFilters = JSON.parse(storedFilters);
                    commit("SET_FILTERS", parsedFilters);
                }
            } catch (error) {
            }
            const filters = await V2_API.getContactListFilters();
            try {
                const cloneFilters = _.cloneDeep(filters);
                cloneFilters.forEach((filter) => {
                    filter.count = 0;
                })
                localStorage.setItem(storageKey, JSON.stringify(cloneFilters));
            } catch (error) {
            }
            
            commit("SET_FILTERS", filters);
        },
        async getContactListGroups({ state, getters }, { groupField, statusState, filters = [] }) {
            const selectedFilter = getters.selectedFilter;
            const filterType = selectedFilter?.type;
            const filterId = selectedFilter?.id;
            const selectedState = statusState || state.selectedState;

            const params = {
                metadata: {
                    source: "TableContactList.getContactListGroups",
                },
                group: {
                    field: groupField,
                },
                state: selectedState,
            };

            if (filters.length > 0) {
                params.filters = filters;
            }

            if (filterType && filterId) {
                params.inbox = {
                    type: filterType,
                    id: filterId,
                };
            }

            const data = await V2_API.getContactListGroups(params);
            return data;
        },
        async getContact({ commit, state }, { contactId, cache = true, ignoreSource }) {
            const cachedContact = state.cachedContacts[contactId];
            const now = Date.now();
            const TTL_MS = 5 * 60 * 1000; // 5 dk
            if (cache && cachedContact) {
                const fetchedAt = cachedContact._createdAt ? new Date(cachedContact._createdAt).getTime() : null;
                if (fetchedAt && now - fetchedAt < TTL_MS) {
                    return cachedContact;
                }
            }

            const contact = await V2_API.getContact(contactId, ignoreSource);
            const contactWithTimestamp = {
                ...contact,
                inited: true,
                _createdAt: new Date(),
            };

            Vue.set(state.cachedContacts, contactId, contactWithTimestamp);
            return contactWithTimestamp;
        },
        async fetchContact({ state, commit }, { contactId, ignoreSource, viewed = false }) {
            const contact = await V2_API.getContact(contactId, ignoreSource, viewed);
            const contactWithTimestamp = {
                ...contact,
                inited: true,
                _createdAt: new Date(),
            };
            if (viewed) {
                commit("SET_CONTACT_UNREAD", {
                    contactId,
                    unread: false,
                });
            }
            Vue.set(state.cachedContacts, contactId, contactWithTimestamp);
            return contactWithTimestamp;
        },
        updateContactListFilters({ commit }, filters) {
            commit("SET_FILTERS", filters);
        },
        addContact({ commit }, contact) {
            commit("ADD_CONTACT", contact);
        },
        removeContact({ commit }, contactId) {
            commit("REMOVE_CONTACT", contactId);
        },
        updateContact({ commit }, contact) {
            commit("UPDATE_CONTACT", contact);
        },
        appendMessage({ commit, rootState }, payload) {
            const windows = rootState.app && rootState.app.windows;
            commit("APPEND_MESSAGE", Object.assign({}, payload, { windows }));
        },
        appendMultiMessage({ commit, rootState }, payload) {
            const windows = rootState.app && rootState.app.windows;
            commit("APPEND_MULTI_MESSAGE", Object.assign({}, payload, { windows }));
        },
        updateMessagePartial({ commit }, payload) {
            commit("UPDATE_MESSAGE_PARTIAL", payload);
        },
        setMessageDeleted({ commit }, payload) {
            commit("SET_MESSAGE_DELETED", payload);
        },
        updateContactFields({ commit }, payload) {
            commit("UPDATE_CONTACT_FIELDS", payload);
        },
        setSelectedContact({ commit, state }, selectedContact) {
            const previousContactId = state.selectedContact?._id;
            if (previousContactId && previousContactId !== selectedContact._id) {
                commit("TRIM_CONTACT_MESSAGES", previousContactId);
            }
            
            commit("SET_SELECTED_CONTACT", selectedContact);
            commit("SET_CONTACT_UNREAD", {
                contactId: selectedContact._id,
                unread: false,
            });
        },
        setSelectedState({ commit }, selectedState) {
            commit("SET_SELECTED_STATE", selectedState);
        },
        setSelectedSortField({ commit }, selectedSortField) {
            commit("SET_SELECTED_SORT_FIELD", selectedSortField);
        },
        setSelectedSortDir({ commit }, selectedSortDir) {
            commit("SET_SELECTED_SORT_DIR", selectedSortDir);
        },
        setActiveView({ commit }, activeView) {
            commit("SET_ACTIVE_VIEW", activeView);
        },
        setSelectedFilterId({ commit }, selectedFilterId) {
            commit("SET_SELECTED_FILTER_ID", selectedFilterId);
        },
        setMultiChatView({ commit, state }, data) {
            if (!data.enabled && state.multiChatView.enabled && Array.isArray(state.multiChatView.contacts)) {
                const previousContactIds = state.multiChatView.contacts.map((c) => c._id).filter(Boolean);
                if (previousContactIds.length > 0) {
                    commit("TRIM_CONTACT_MESSAGES", previousContactIds);
                }
            }
            else if (data.enabled && state.multiChatView.enabled && 
                     data.type !== state.multiChatView.type && 
                     Array.isArray(state.multiChatView.contacts)) {
                const previousContactIds = state.multiChatView.contacts.map((c) => c._id).filter(Boolean);
                if (previousContactIds.length > 0) {
                    commit("TRIM_CONTACT_MESSAGES", previousContactIds);
                }
            }
            
            commit("SET_MULTI_CHAT_VIEW", data);
        },
        setWhatsappContactStatus({ commit }, payload) {
            commit("SET_WHATSAPP_CONTACT_STATUS", payload);
        },
        trimContactMessages({ commit }, contactIds) {
            commit("TRIM_CONTACT_MESSAGES", contactIds);
        },
        async listContactMessages({ state, commit, dispatch }, { contactId, conversationId, cache = false }) {
            const PREFERRED_LIMIT = 50;
            const bucket = state.messagesByContact[contactId];
            
            if (cache && bucket && bucket.data && bucket.data.length > 0) {
                return bucket.data;
            }

            const request = bucket || {
                data: [],
                totalPage: 0,
                totalCount: 0,
                offset: -PREFERRED_LIMIT,
                limit: PREFERRED_LIMIT,
                ts: Date.now(),
                loading: false,
            };

            // Set loading
            Vue.set(state.messagesByContact, contactId, {
                ...request,
                loading: true,
            });

            const nextOffset = request.data.filter((m) => m.messageType !== "conversation-separator").length;
            try {
                const response = await listContactMessagesV3(contactId, nextOffset, PREFERRED_LIMIT);
                const { data, totalPage, totalCount } = response.data;

                const mergedData = _.chain(request.data)
                    .concat(data)
                    .uniqBy("uniqMessageId")
                    .orderBy("createdDate", "asc")
                    .value();

                let lastTS = _.get(_.orderBy(mergedData, "createdDate", "desc"), "0.createdDate");
                lastTS = lastTS ? moment(lastTS).valueOf() : Date.now();

                Vue.set(state.messagesByContact, contactId, {
                    data: mergedData,
                    totalPage,
                    totalCount,
                    offset: nextOffset,
                    limit: PREFERRED_LIMIT,
                    ts: lastTS,
                    loading: false,
                });

                // Diğer contact'ların mesajlarını da appendMultiMessage ile ekle
                if (data.length > 0) {
                    dispatch("appendMultiMessage", {
                        contactId,
                        messages: data,
                    });
                }

                return mergedData;
            } catch (err) {
                Vue.set(state.messagesByContact, contactId, {
                    ...request,
                    loading: false,
                });
                return [];
            }
        },
        async getFilterStats({ state, getters }, payload = {}) {
            const selectedFilterId = payload.selectedFilterId || state.selectedFilterId;
            const filters = state.filters;
            const selectedFilter = filters.find((f) => f.id === selectedFilterId);
            
            if (!selectedFilter) {
                console.warn("getFilterStats -> selectedFilter not found", { selectedFilterId });
                return null;
            }

            const requestPayload = {
                inbox: {
                    type: selectedFilter.type || "system",
                    id: selectedFilter._id || selectedFilter.id,
                },
                filters: selectedFilter.filters || [],
                group: null,
                search: null,
                sort: null,
                metadata: {
                    source: payload.source || "store.getFilterStats",
                },
            };

            try {
                const result = await V2_API.getFilterStats(requestPayload);
                return result;
            } catch (err) {
                console.error("getFilterStats error", err);
                return null;
            }
        },
    },
};

export default contact;
